// Participant-level state — mirrors IndexedDB "participant_state" (Section 6).
// IndexedDB remains the source of truth; this store is a reactive in-memory
// cache so screens can subscribe without re-reading IDB on every render.

import { create } from 'zustand';
import {
  getParticipantState,
  patchParticipantState,
  putParticipantState,
  DEFAULT_STATE,
} from '../db/indexedDB';

export const useParticipantStore = create((set, get) => ({
  ...DEFAULT_STATE,
  loaded: false,

  async hydrate() {
    const state = await getParticipantState();
    set({ ...state, loaded: true });
  },

  async patch(partial) {
    const next = await patchParticipantState(partial);
    set(next);
    return next;
  },

  async setParticipantId(participantId) {
    return get().patch({ participant_id: participantId });
  },

  async setSchedule(schedule) {
    return get().patch({ schedule });
  },

  async advanceAfterSession() {
    const { session_number, content_index } = get();
    return get().patch({
      session_number: session_number + 1,
      content_index: (content_index + 1) % Number(import.meta.env.VITE_TOTAL_SESSIONS ?? 35),
      active_in_session: false,
      current_session_id: null,
      last_session_ts: new Date().toISOString(),
    });
  },

  /** Full reset used by the researcher panel when changing participant ID. */
  async resetForNewParticipant(participantId) {
    const fresh = { ...DEFAULT_STATE, participant_id: participantId, pwa_install_date: get().pwa_install_date };
    await putParticipantState(fresh);
    set({ ...fresh, loaded: true });
    return fresh;
  },
}));
